import { useEffect, useMemo, useState } from "react";
import { Lock, X, Delete } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * PIN-only personnel verification modal.
 *
 * Shown before saving (or updating) an order. The user types only the PIN —
 * the system matches the PIN to the personnel and stamps their name on the order.
 *
 * Props:
 *  - open: boolean
 *  - busy: boolean (parent is submitting)
 *  - title: string (header)
 *  - submitLabel: string (CTA, e.g. "Kaydet" or "Güncelle")
 *  - onClose: () => void
 *  - onSubmit: (pin: string) => Promise<void> | void
 */
export function ClerkPinModal({ open, busy, title = "Şifre Girin", submitLabel = "Onayla", onClose, onSubmit }) {
  const [pin, setPin] = useState("");

  useEffect(() => {
    if (!open) setPin("");
  }, [open]);

  if (!open) return null;

  const handleDigit = (d) => {
    if (pin.length >= 12 || busy) return;
    setPin((p) => p + d);
  };

  const handleBackspace = () => {
    if (busy) return;
    setPin((p) => p.slice(0, -1));
  };

  const handleConfirm = () => {
    if (busy) return;
    if (pin.length < 4) {
      toast.error("Şifre en az 4 hane olmalı");
      return;
    }
    onSubmit(pin);
  };

  return (
    <div
      data-testid="clerk-pin-modal"
      className="fixed inset-0 z-[60] bg-zinc-950/80 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-zinc-950 brutal-shadow-dark w-full sm:max-w-sm max-h-[100dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-zinc-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-zinc-950 text-white flex items-center justify-center">
              <Lock strokeWidth={3} size={14} />
            </div>
            <h2 className="text-base font-black tracking-tight text-zinc-950 uppercase">{title}</h2>
          </div>
          <button data-testid="close-clerk-modal" onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-950" disabled={busy}>
            <X size={18} />
          </button>
        </div>

        <PinPad
          pin={pin}
          busy={busy}
          submitLabel={submitLabel}
          onDigit={handleDigit}
          onBackspace={handleBackspace}
          onConfirm={handleConfirm}
        />
      </div>
    </div>
  );
}

function PinPad({ pin, busy, submitLabel, onDigit, onBackspace, onConfirm }) {
  const digits = useMemo(() => ["1","2","3","4","5","6","7","8","9"], []);
  return (
    <div className="p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 text-center">
        Personel şifrenizi girin
      </div>

      <div data-testid="pin-display" className="flex items-center justify-center gap-2 mb-5 h-12">
        {Array.from({ length: Math.max(pin.length, 4) }).map((_, i) => (
          <div
            key={i}
            className={`w-4 h-4 border-2 ${
              i < pin.length ? "bg-zinc-950 border-zinc-950" : "border-zinc-300"
            }`}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {digits.map((d) => (
          <button
            key={d}
            type="button"
            data-testid={`pin-key-${d}`}
            onClick={() => onDigit(d)}
            disabled={busy}
            className="py-4 bg-white border-2 border-zinc-200 text-2xl font-black text-zinc-950 hover:bg-zinc-50 hover:border-zinc-950 active:translate-y-[1px] transition-all disabled:opacity-50"
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          data-testid="pin-key-back"
          onClick={onBackspace}
          disabled={busy || pin.length === 0}
          className="py-4 bg-white border-2 border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:border-zinc-950 active:translate-y-[1px] flex items-center justify-center transition-all disabled:opacity-30"
        >
          <Delete size={20} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          data-testid="pin-key-0"
          onClick={() => onDigit("0")}
          disabled={busy}
          className="py-4 bg-white border-2 border-zinc-200 text-2xl font-black text-zinc-950 hover:bg-zinc-50 hover:border-zinc-950 active:translate-y-[1px] transition-all disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          data-testid="confirm-pin"
          onClick={onConfirm}
          disabled={busy || pin.length < 4}
          className="py-4 bg-emerald-600 text-white font-black uppercase tracking-widest text-xs border-b-4 border-emerald-900 active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "..." : submitLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * Helper hook to verify a PIN via the backend.
 * Resolves to a `{ id, name }` clerk on success, throws on invalid PIN.
 */
export function useClerkVerify() {
  return async (pin) => {
    const { data } = await api.post("/clerks/verify-pin", { pin });
    return data;
  };
}

/**
 * Custom hook — fetches clerks list once for parent components (admin views).
 */
export function useClerks() {
  const [clerks, setClerks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refetch = async () => {
    try {
      const { data } = await api.get("/clerks");
      setClerks(data);
    } catch (e) {
      console.warn("Clerks fetch failed:", formatApiError(e));
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { refetch(); }, []);

  return { clerks, loaded, refetch };
}
